import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signIn, supabase, resendConfirmationEmail, signInWithGoogle } from '../../utils/supabase';
import { EyeIcon, EyeSlashIcon, EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import AuthAside from './AuthAside';
// Thème citron / noir / blanc des pages d'authentification (classes préfixées "auth-")
import './AuthPages.css';

/**
 * Transforme les erreurs techniques renvoyées par Supabase en messages simples
 * et utiles pour la personne qui essaie de se connecter. Le message lié aux
 * identifiants reste volontairement générique afin de ne pas indiquer si une
 * adresse e-mail existe ou non dans l'application.
 *
 * @param {Error & { code?: string }} error - Erreur retournée par Supabase ou par le navigateur.
 * @returns {string} Le message en français à afficher dans le formulaire.
 */
const getLoginErrorMessage = (error) => {
  const errorCode = error?.code?.toLowerCase();
  const errorMessage = error?.message?.toLowerCase() || '';

  if (errorCode === 'invalid_credentials' || errorMessage.includes('invalid login credentials')) {
    return 'L’adresse e-mail ou le mot de passe est incorrect.';
  }

  if (errorCode === 'email_not_confirmed' || errorMessage.includes('email not confirmed')) {
    return 'Votre adresse e-mail n’a pas encore été confirmée. Vérifiez votre boîte de réception.';
  }

  if (errorCode === 'over_request_rate_limit' || errorCode === 'over_email_send_rate_limit' || errorMessage.includes('too many requests')) {
    return 'Trop de tentatives ont été effectuées. Veuillez réessayer dans quelques minutes.';
  }

  return 'La connexion a échoué. Veuillez réessayer.';
};

// Arguments de vente affichés dans le panneau noir (uniquement des fonctions existantes)
const AUTH_HIGHLIGHTS = [
  'Commandes, stocks et clients dans un seul tableau de bord',
  'Livraisons assignées et suivies par zone',
  'Caisse (POS) connectée à votre boutique',
  'Assistant commercial et analyses par IA'
];

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(location.state?.message || null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const redirect = new URLSearchParams(location.search).get('redirect');

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        console.log('User signed in, redirecting to dashboard');
        navigate(redirect || '/dashboard', { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, redirect]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
    setSuccess(null);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Attempting Google sign in...');
      const { error } = await signInWithGoogle({
        redirectTo: redirect ? `${window.location.origin}${redirect}` : undefined
      });
      
      if (error) {
        console.error('Google sign in error:', error);
        throw error;
      }
      
      console.log('Google sign in initiated successfully');
    } catch (err) {
      console.error('Google authentication error:', err);
      setError(err.message || 'Erreur lors de la connexion avec Google');
      setGoogleLoading(false);
    }
  };

  const handleResendConfirmation = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Veuillez entrer votre email pour recevoir un nouveau lien de confirmation');
      return;
    }
    
    setResendLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await resendConfirmationEmail(formData.email);
      if (error) throw error;
      setSuccess('Un nouveau lien de confirmation a été envoyé à votre adresse email');
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'envoi du lien de confirmation');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      console.log('Attempting to sign in with:', formData.email);
      const { data, error } = await signIn(formData);
      console.log('Sign in result:', { data, error });
      
      if (error) {
        console.error('Sign in error:', error);
        throw error;
      }
      
      // The navigation will be handled by the onAuthStateChange listener
    } catch (err) {
      console.error('Login error:', err);
      setError(getLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-shell">
        {/* Panneau de présentation (masqué sur mobile) */}
        <AuthAside titre="Gérez vos ventes WhatsApp, de la commande à la" motCle="livraison" points={AUTH_HIGHLIGHTS} />

        {/* Formulaire de connexion */}
        <section className="auth-card">
          <h2>Bienvenue</h2>
          <p className="auth-sub">Connectez-vous à votre compte</p>

          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="auth-btn auth-btn-outline"
          >
            {googleLoading ? (
              <>
                <span className="auth-spinner auth-spinner-dark" />
                Connexion en cours...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuer avec Google
              </>
            )}
          </button>

          <div className="auth-divider"><span>ou continuer avec email</span></div>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="auth-alert auth-alert-error" role="alert">{error}</div>}
            {success && <div className="auth-alert auth-alert-success" role="status">{success}</div>}

            <label htmlFor="email" className="auth-label">Email</label>
            <div className="auth-field">
              <EnvelopeIcon className="auth-field-icon" />
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="votre.email@exemple.com"
              />
            </div>

            <label htmlFor="password" className="auth-label">Mot de passe</label>
            <div className="auth-field">
              <LockClosedIcon className="auth-field-icon" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="auth-eye"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </button>
            </div>

            {/* Ouvre le parcours Supabase de récupération sans modifier la connexion existante. */}
            <Link to="/forgot-password" className="auth-link-btn">Mot de passe oublié ?</Link>

            <button type="submit" disabled={loading} className="auth-btn auth-btn-accent">
              {loading ? (
                <>
                  <span className="auth-spinner" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </button>

            <button
              type="button"
              onClick={handleResendConfirmation}
              disabled={resendLoading}
              className="auth-link-btn"
            >
              {resendLoading ? 'Envoi en cours...' : 'Renvoyer l\'email de confirmation'}
            </button>
          </form>

          <p className="auth-switch">
            Pas encore inscrit ? <Link to="/register">Créer un compte</Link>
          </p>
        </section>
      </div>
    </div>
  );
};

export default LoginForm;
